import {
	useGetProductPricesQuery,
	useSetOrderQuoteOfferPricingMutation,
	useSuggestOrderQuoteOfferPricingQuery,
	useUpdateOrderQuoteOfferMutation,
} from '@codezo/graphql';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import type { CatalogPriceRung } from '../../pricing/catalog-types';
import {
	appliedRung,
	catalogueFill,
	useCatalogSuggestions,
	type QuotedPrice,
} from '../../pricing/catalog-suggestions';
import { CatalogCoverageBanner } from '../../pricing/CatalogCoverageBanner';
import type { CatalogOption } from '../../pricing/CatalogSourcePicker';
import { PriceRowCard } from '../../pricing/PriceRowCard';
import { TierFeasibility } from '../../pricing/TierFeasibility';
import {
	belowCost,
	isUnpriced,
	markupFromSale,
	parseAmount,
	pricingPayload,
	type TierEntryMode,
} from '../../pricing/tier-pricing';
import { useCurrencyStore } from '../../../stores/currency';
import { VetterSelector } from '../../selectors/VetterSelector';

type PricingRowValues = {
	/** `baseUnitPrice` — what we pay. */
	cost: string;
	/** `unitSalePrice` — what the client pays. The primary input. */
	salePrice: string;
	/** `markupPercentage` — the secondary way in; kept in step with the price. */
	markup: string;
	/** Which of the two the admin typed. Only that one is sent. */
	entryMode: TierEntryMode;
	minDays: string;
	maxDays: string;
	/** The catalogue price these figures came from — quantity and supplier. */
	sourceQuantity?: number | null;
	sourceVetterEntityId?: string | null;
	/** Set once the admin acts — until then a suggestion shows as a ghost. */
	touched?: boolean;
	/** Filled by the exact-match rule rather than by a person. */
	autoApplied?: boolean;
	/** What was really quoted when the cost was converted into this form's currency. */
	quoted?: QuotedPrice | null;
};

/** A saved tier, as the modal loads it. */
export type ExistingTierPricing = {
	orderQuoteItemQuantityId: string;
	/** Cost in the header currency — what the form shows. */
	baseUnitPrice?: number | null;
	/** What the client pays per unit, in the header currency. */
	unitPrice?: number | null;
	markupPercentage?: number | null;
	/** The currency the tier was actually typed in. Null means unknown. */
	authoredCurrency?: string | null;
	minProductionDays?: number | null;
	maxProductionDays?: number | null;
};

const emptyPricingRow: PricingRowValues = {
	cost: '',
	salePrice: '',
	markup: '',
	entryMode: 'sale',
	minDays: '',
	maxDays: '',
};

/**
 * Prices every quantity tier of one offer.
 *
 * The catalogue fills the form and never sets a price: suggestions arrive as
 * ghost placeholders in the fields they would fill, and only writing them —
 * with Use, or by picking another break — puts a number in the tier.
 */
export function SetPricingModal({
	open,
	onOpenChange,
	offerId,
	quantities,
	existingPricing,
	productId,
	vetterEntityId,
	vetterName,
	deadline,
	acceptDeadline,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	offerId: string;
	quantities: Array<{ id: string; quantity: number }>;
	/** The product being priced — its whole catalogue feeds the picker. */
	productId: string;
	/** The offer's supplier, if one is assigned. Decides which prices are usable. */
	vetterEntityId?: string | null;
	vetterName?: string | null;
	existingPricing: ExistingTierPricing[];
	/** Deadline to judge production feasibility against. */
	deadline?: string | null;
	/** Offer accept-by date (worst-case anchor), ISO string. */
	acceptDeadline?: string | null;
	onSuccess: () => void;
}) {
	const [values, setValues] = useState<Record<string, PricingRowValues>>({});
	/*
	 * One currency, the header's, like the page around it: saved tiers load
	 * converted and every tier saves in it, touched or not. The backend keeps
	 * any figure within half a cent of what is stored exactly as stored, so an
	 * untouched save never re-prices a tier.
	 */
	const { selectedCurrency: currency } = useCurrencyStore();
	const [setPricing, { loading }] = useSetOrderQuoteOfferPricingMutation();
	const [updateOffer, { loading: assigning }] = useUpdateOrderQuoteOfferMutation();

	/*
	 * The supplier as this modal knows it. Starts as the offer's, and moves the
	 * moment the admin reassigns — saved straight away, because the suggestions
	 * are worked out server-side from the offer's saved supplier.
	 */
	const [assignedVetterId, setAssignedVetterId] = useState<string | null>(null);
	const [assignedVetterName, setAssignedVetterName] = useState<string | null>(null);
	/** Another supplier's price picked while one is assigned — waits on a confirm. */
	const [pendingSwap, setPendingSwap] = useState<{
		quantityId: string;
		option: CatalogOption;
	} | null>(null);
	/* The reassignment is saved even if the pricing is then cancelled, so the
	   offer list has to reload on close either way. */
	const supplierChanged = useRef(false);

	useEffect(() => {
		if (!open) return;
		setAssignedVetterId(vetterEntityId ?? null);
		setAssignedVetterName(vetterName ?? null);
		supplierChanged.current = false;
	}, [open, vetterEntityId, vetterName]);

	/**
	 * What the catalogue would charge for these tiers. A prefill for the form,
	 * never a value in its own right — the offer owns its numbers from the moment
	 * this modal saves. An unreachable catalogue costs a prefill, never a quote,
	 * so a failure here is ignored rather than surfaced.
	 */
	const { data: suggestionData, refetch: refetchSuggestions } =
		useSuggestOrderQuoteOfferPricingQuery({
			// Every suggestion comes back restated in the currency this form saves in.
			variables: { orderQuoteOfferId: offerId, inCurrency: currency },
			skip: !open,
			fetchPolicy: 'no-cache',
		});
	const suggestionList = useMemo(
		() => suggestionData?.suggestOrderQuoteOfferPricing ?? [],
		[suggestionData]
	);

	/*
	 * EVERY catalogue price on the product, not only the ladder the lookup
	 * resolved: a supplier's prices should be visible before they are assigned,
	 * even though assigning is what makes them usable.
	 */
	const { data: catalogueData } = useGetProductPricesQuery({
		variables: { input: { productId }, currency },
		skip: !open || !productId,
		fetchPolicy: 'no-cache',
	});
	const cataloguePrices = catalogueData?.getProductPrices ?? [];

	const { suggestions, optionsFor, exactFills } = useCatalogSuggestions({
		list: suggestionList,
		cataloguePrices,
		assignedVetterId,
		formCurrency: currency,
	});

	/** Fills a tier from ONE rung of the ladder — the applied break or any other. */
	const applySuggestion = (qId: string, rung: CatalogPriceRung) => {
		setValues((prev) => {
			const row = prev[qId] || emptyPricingRow;
			const fill = catalogueFill(rung, currency, row);
			// No figure in this form's currency (no rate for the pair): nothing to fill.
			if (!fill) return prev;
			return { ...prev, [qId]: { ...row, ...fill, touched: true, autoApplied: false } };
		});
	};

	useEffect(() => {
		if (!open) return;
		const initial: Record<string, PricingRowValues> = {};
		for (const q of quantities) {
			const existing = existingPricing.find((p) => p.orderQuoteItemQuantityId === q.id);
			const priced = existing?.baseUnitPrice != null;
			const cost = priced ? String(existing.baseUnitPrice) : '';
			/* Sale price is the form's primary number, and every saved tier has one
			   however it was authored — including the fixed-amount tiers this form
			   used to produce. Reading `unitPrice` rather than re-deriving from the
			   markup keeps those round-tripping unchanged. */
			const salePrice = existing?.unitPrice != null ? String(existing.unitPrice) : '';
			initial[q.id] = {
				cost,
				salePrice,
				markup: salePrice
					? markupFromSale(cost, salePrice)
					: existing?.markupPercentage != null
						? String(existing.markupPercentage)
						: '',
				entryMode: 'sale',
				minDays:
					existing?.minProductionDays != null ? String(existing.minProductionDays) : '',
				maxDays:
					existing?.maxProductionDays != null ? String(existing.maxProductionDays) : '',
				// A tier that already carries figures is not awaiting a suggestion.
				touched: priced,
			};
		}
		setValues(initial);
	}, [open, quantities, existingPricing]);

	/**
	 * An exact match fills itself — same rule as the wizard.
	 *
	 * Exact means volume AND source: this offer's supplier answering with its own
	 * row, or the no-supplier row when the offer has no supplier. A fallback is a
	 * suggestion, not an answer, so it still waits to be taken.
	 *
	 * A tier that already carries a saved price is `touched` and left alone —
	 * this only fills the ones that arrived empty.
	 */
	useEffect(() => {
		if (!open || exactFills.length === 0) return;
		setValues((prev) => {
			const next = { ...prev };
			let changed = false;
			for (const { quantityId, fill } of exactFills) {
				const row = next[quantityId] || emptyPricingRow;
				if (row.touched && !row.autoApplied) continue;
				if (row.cost === fill.cost && row.markup === fill.markup && row.autoApplied)
					continue;
				next[quantityId] = { ...row, ...fill, touched: true, autoApplied: true };
				changed = true;
			}
			// Returning `prev` unchanged is what stops this re-running forever.
			return changed ? next : prev;
		});
	}, [open, exactFills]);

	const catalogueBreaks = [...new Set(cataloguePrices.map((r) => r.quantity))].sort(
		(a, b) => a - b
	);

	/** Saves the offer's supplier. Null unassigns. */
	const assignVetter = async (id: string | null, name: string | null) => {
		try {
			const { data } = await updateOffer({
				variables: { id: offerId, input: { vetterEntityId: id } },
			});
			const res = data?.updateOrderQuoteOffer;
			if (res && !res.success) throw new Error(res.message || 'Failed to assign vetter');
			setAssignedVetterId(id);
			setAssignedVetterName(name);
			supplierChanged.current = true;
			await refetchSuggestions();
			return true;
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to assign vetter');
			return false;
		}
	};

	/**
	 * Picking a supplier's price IS choosing that supplier — otherwise the offer
	 * quotes a figure under someone else's name. With nobody assigned that just
	 * happens; replacing someone asks first.
	 */
	const pickOption = async (quantityId: string, option: CatalogOption) => {
		const owner = option.vetterEntityId ?? null;
		if (owner == null || owner === assignedVetterId) {
			applySuggestion(quantityId, option);
			return;
		}
		if (assignedVetterId) {
			setPendingSwap({ quantityId, option });
			return;
		}
		if (await assignVetter(owner, option.vetterName ?? null)) {
			applySuggestion(quantityId, option);
		}
	};

	const confirmSwap = async () => {
		if (!pendingSwap) return;
		const { quantityId, option } = pendingSwap;
		if (await assignVetter(option.vetterEntityId ?? null, option.vetterName ?? null)) {
			applySuggestion(quantityId, option);
		}
		setPendingSwap(null);
	};

	const handleOpenChange = (next: boolean) => {
		if (!next && supplierChanged.current) onSuccess();
		onOpenChange(next);
	};

	const pendingCount = quantities.filter(
		(q) => suggestions.has(q.id) && !values[q.id]?.touched
	).length;

	const applyAllSuggestions = () => {
		for (const q of quantities) {
			const sug = suggestions.get(q.id);
			if (sug && !values[q.id]?.touched) applySuggestion(q.id, appliedRung(sug));
		}
	};

	/* The backend rejects a sale price under cost outright. Caught here so the
	   admin sees which tier rather than losing the whole save to an error. */
	const underwater = quantities.filter((q) => {
		const v = values[q.id];
		return v?.cost && v.entryMode === 'sale' && belowCost(v.cost, v.salePrice);
	});

	/* A tier with a cost and no client price saves fine, but the offer can't be
	   sent until it has one, so the footer says so. */
	const unpriced = quantities.filter((q) => {
		const v = values[q.id];
		return v && isUnpriced(v);
	});

	const blocked = underwater.length > 0;

	const handleSubmit = async () => {
		if (blocked) return;
		try {
			/* parseAmount, not parseFloat: `0,16` from a Danish keyboard reads as 0
			   to parseFloat, which dropped the tier here — and the backend deletes
			   any saved tier the input leaves out. */
			const items = quantities
				.filter((q) => (parseAmount(values[q.id]?.cost ?? '') ?? 0) > 0)
				.map((q) => {
					const v = values[q.id];
					return {
						orderQuoteItemQuantityId: q.id,
						...pricingPayload({ ...v, currency }),
						minProductionDays: v.minDays ? parseInt(v.minDays) : undefined,
						maxProductionDays: v.maxDays ? parseInt(v.maxDays) : undefined,
					};
				});

			if (items.length === 0) return;

			const { data } = await setPricing({
				variables: {
					input: {
						orderQuoteOfferId: offerId,
						items,
					},
				},
			});
			const res = data?.setOrderQuoteOfferPricing;
			if (res && !res.success) throw new Error(res.message || 'Failed to save pricing');
			toast.success('Pricing saved');
			supplierChanged.current = false;
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save pricing');
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			{/* Wider than a form column: the point of the table is seeing the tiers
			    beside each other, which a max-w-md modal cannot do. */}
			<DialogContent className="sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>Set pricing</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-3">
							<div className="flex items-center justify-between gap-3">
								<span className="text-xs text-text-3">Vetter</span>
								<div className="w-64">
									{/* Keyed on the assignment: picking a supplier's price
									    moves it from outside, and the selector would
									    otherwise keep showing the name picked in it. */}
									<VetterSelector
										key={assignedVetterId ?? 'none'}
										value={assignedVetterId ?? ''}
										selectedLabel={assignedVetterName ?? undefined}
										onChange={(id, label) => {
											if ((id || null) === assignedVetterId) return;
											// Pass the label on: the remounted selector only
											// resolves ids on its first page, which a vetter
											// created just now may not be on.
											void assignVetter(id || null, label ?? null);
										}}
										placeholder={assigning ? 'Assigning…' : 'Assign vetter'}
										clearable
										creatable
									/>
								</div>
							</div>

							<CatalogCoverageBanner
								priceCount={catalogueBreaks.length}
								lowestQuantity={catalogueBreaks[0] ?? 0}
								highestQuantity={catalogueBreaks[catalogueBreaks.length - 1] ?? 0}
								pendingCount={pendingCount}
								tierCount={quantities.length}
								hasSupplier={!!assignedVetterId}
								onApplyAll={applyAllSuggestions}
							/>

							{quantities.map((q) => {
								const vals = values[q.id] || emptyPricingRow;
								return (
									<PriceRowCard
										key={q.id}
										quantity={q.quantity}
										currency={currency}
										options={optionsFor(q.id)}
										assignable
										values={vals}
										authoredCurrency={
											existingPricing.find(
												(p) => p.orderQuoteItemQuantityId === q.id
											)?.authoredCurrency
										}
										onChange={(next) =>
											setValues((prev) => ({ ...prev, [q.id]: next }))
										}
										onPick={(option) => void pickOption(q.id, option)}
										extra={
											<TierFeasibility
												minDays={vals.minDays}
												maxDays={vals.maxDays}
												deadline={deadline}
												acceptDeadline={acceptDeadline}
											/>
										}
									/>
								);
							})}
						</div>
					</DialogBody>
					<DialogFooter>
						{unpriced.length > 0 && (
							<span className="mr-auto text-2xs text-text-4">
								{unpriced.length === 1
									? '1 tier has no client price yet. Set it any time before sending.'
									: `${unpriced.length} tiers have no client price yet. Set them any time before sending.`}
							</span>
						)}
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={loading || assigning || blocked}
							title={
								underwater.length > 0
									? 'A sale price is below cost — the backend rejects that.'
									: undefined
							}
						>
							{loading ? 'Saving...' : 'Save pricing'}
						</Button>
					</DialogFooter>
				</DialogForm>

				<ConfirmDialog
					open={!!pendingSwap}
					onOpenChange={(v) => !v && setPendingSwap(null)}
					title="Change vetter?"
					description={
						<>
							This offer is assigned to {assignedVetterName ?? 'another vetter'}.
							Using {pendingSwap?.option.vetterName ?? 'this'}&apos;s price makes them
							the vetter for this offer. Tiers already priced from{' '}
							{assignedVetterName ?? 'the current vetter'}&apos;s catalogue keep their
							figures — check them before saving.
						</>
					}
					confirmLabel="Change vetter"
					variant="default"
					loading={assigning}
					onConfirm={() => void confirmSwap()}
				/>
			</DialogContent>
		</Dialog>
	);
}

/** One quantity tier: pricing inputs + a live production-only feasibility floor. */
